import React, { useRef, useEffect } from 'react';
import { Send, Star, Trash2, Copy, Bot, RotateCcw } from 'lucide-react';
import { CachedPrompt } from '../hooks/usePromptCache';
import { ModelSelectorPopover } from './ModelSelectorPopover';

interface QueryInputProps {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onSubmit: (mode?: 'continue' | 'new') => void;
  isLoading: boolean;
  disabled?: boolean;
  cachedPrompts?: CachedPrompt[];
  onRemovePrompt?: (id: string) => void;
  onAddPrompt?: (text: string) => void;
  onOpenModelSelector?: () => void;
  selectedModelsCount?: number;
  customConfigName?: string;
  isModelSelectorOpen?: boolean;
  modelSelectorContent?: React.ReactNode;
  onSelectAll?: () => void;
  usedSessionContext?: boolean;
  sessionCanContinue?: boolean;
  sessionLimitReached?: boolean;
  onNewSession?: () => void;
}

export const QueryInput: React.FC<QueryInputProps> = ({
  prompt,
  onPromptChange,
  onSubmit,
  isLoading,
  disabled = false,
  cachedPrompts = [],
  onRemovePrompt,
  onAddPrompt,
  onOpenModelSelector,
  selectedModelsCount = 1,
  customConfigName: _customConfigName,
  isModelSelectorOpen = false,
  modelSelectorContent,
  onSelectAll,
  usedSessionContext = false,
  sessionCanContinue = false,
  sessionLimitReached = false,
  onNewSession
}) => {
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [showValidationMessage, setShowValidationMessage] = React.useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modelButtonRef = useRef<HTMLButtonElement>(null);

  const isPromptFavorited = cachedPrompts.some(p => p.text === prompt.trim());
  
  // Validation logic
  const hasPrompt = prompt.trim().length > 0;
  const hasModels = selectedModelsCount > 0;
  const canSubmit = hasPrompt && hasModels && !disabled;
  
  const getValidationMessage = (): string | null => {
    if (disabled) return null;
    if (!hasPrompt && !hasModels) return 'Please enter a question and select at least one AI model';
    if (!hasPrompt) return 'Please enter a question';
    if (!hasModels) return 'Please select at least one AI model';
    return null;
  };

  const handleToggleFavorite = () => {
    if (!prompt.trim()) return;
    
    if (isPromptFavorited) {
      const cachedPrompt = cachedPrompts.find(p => p.text === prompt.trim());
      if (cachedPrompt && onRemovePrompt) {
        onRemovePrompt(cachedPrompt.id);
      }
    } else {
      if (onAddPrompt) {
        onAddPrompt(prompt.trim());
      }
    }
  };

  const handleCopyPrompt = async (promptText: string, id: string) => {
    try {
      await navigator.clipboard.writeText(promptText);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy prompt:', err);
    }
  };

  const handleSubmit = (e: React.FormEvent, mode?: 'continue' | 'new') => {
    e.preventDefault();
    if (canSubmit) {
      setShowValidationMessage(false);
      onSubmit(mode);
    } else {
      setShowValidationMessage(true);
      setTimeout(() => setShowValidationMessage(false), 3000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Only submit if this textarea actually has focus (not when a dialog is open above)
      if (document.activeElement !== textareaRef.current) return;
      e.preventDefault();
      // Enter key continues session if available, otherwise normal submit
      handleSubmit(e, usedSessionContext && sessionCanContinue ? 'continue' : undefined);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [prompt]);

  return (
    <div data-component="QueryInput" className="space-y-3">
      {/* Validation Message */}
      {showValidationMessage && getValidationMessage() && (
        <div data-flow="validation-error" className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
          <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span className="text-sm text-yellow-800">{getValidationMessage()}</span>
        </div>
      )}


      
      {/* Cached Prompts - At top */}
      {cachedPrompts.length > 0 && (
        <div data-flow="cached-prompts">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-4 h-4 text-yellow-500" />
            <span className="text-sm text-gray-600 font-medium">Favorite prompts:</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {cachedPrompts.map((cachedPrompt) => (
              <div
                key={cachedPrompt.id}
                className="flex items-center bg-blue-50 border border-blue-200 rounded-lg overflow-hidden group hover:bg-blue-100 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => onPromptChange(cachedPrompt.text)}
                  disabled={disabled}
                  className="flex-1 px-3 py-2 text-sm text-blue-700 text-left disabled:opacity-50 disabled:cursor-not-allowed transition-colors truncate"
                  title={cachedPrompt.text}
                >
                  {cachedPrompt.text.length > 60 ? `${cachedPrompt.text.substring(0, 60)}...` : cachedPrompt.text}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyPrompt(cachedPrompt.text, cachedPrompt.id);
                  }}
                  disabled={disabled}
                  className="px-2 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors opacity-0 group-hover:opacity-100"
                  title={copiedId === cachedPrompt.id ? "Copied!" : "Copy to clipboard"}
                >
                  <Copy className={`w-4 h-4 ${copiedId === cachedPrompt.id ? 'text-green-500' : ''}`} />
                </button>
                {onRemovePrompt && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemovePrompt(cachedPrompt.id);
                    }}
                    disabled={disabled}
                    className="px-2 py-2 text-red-500 hover:text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors opacity-0 group-hover:opacity-100"
                    title="Remove from favorites"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Query Input - At bottom */}
      <form onSubmit={handleSubmit} className="flex gap-2 items-center">
        {/* Star Button */}
        <button
          type="button"
          onClick={handleToggleFavorite}
          disabled={disabled || !prompt.trim()}
          className={`px-3 py-3 rounded-lg transition-colors flex-shrink-0 ${
            isPromptFavorited
              ? 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={isPromptFavorited ? "Remove from favorites" : "Add to favorites"}
        >
          <Star className={`w-5 h-5 ${isPromptFavorited ? 'fill-current' : ''}`} />
        </button>

        <div className="flex-1 relative flex gap-2 items-center">
          <textarea
            ref={textareaRef}
            value={prompt}
            onChange={(e) => onPromptChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a legal research question..."
            className="flex-1 px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[52px] max-h-32"
            disabled={disabled}
            rows={1}
          />
        </div>
        
        {/* Model Selector Button with Popover */}
        {onOpenModelSelector && (
          <div className="relative">
            <button
              ref={modelButtonRef}
              type="button"
              onClick={onOpenModelSelector}
              disabled={disabled}
              className="px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 flex-shrink-0"
              title="Select AI models"
            >
              <Bot className="w-4 h-4" />
              {selectedModelsCount > 0 && (
                <span className="text-xs px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                  {selectedModelsCount}
                </span>
              )}
            </button>

            {/* Popover - Modal in center of screen */}
            <ModelSelectorPopover
              isOpen={isModelSelectorOpen}
              onClose={onOpenModelSelector}
              onSelectAll={onSelectAll}
              disabled={disabled}
            >
              {modelSelectorContent}
            </ModelSelectorPopover>
          </div>
        )}
        
        {/* Submit Buttons */}
        {usedSessionContext && onNewSession ? (
          <div className="flex gap-1.5 flex-shrink-0">
            <button
              type="button"
              disabled={disabled || !sessionCanContinue}
              onClick={(e) => handleSubmit(e as unknown as React.FormEvent, 'continue')}
              className="px-3 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              title={sessionLimitReached ? 'Session limit reached (5 turns). Start a new session to continue researching.' : (!sessionCanContinue ? 'No successful session to continue' : (!canSubmit && !disabled ? getValidationMessage() || undefined : 'Continue in this session'))}
            >
              <Send className="w-4 h-4" />
              <span className="text-sm">Continue</span>
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={(e) => handleSubmit(e as unknown as React.FormEvent, 'new')}
              className="px-3 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1.5"
              title="Start fresh session and send"
            >
              <RotateCcw className="w-4 h-4" />
              <span className="text-sm">New</span>
            </button>
          </div>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={(e) => handleSubmit(e as unknown as React.FormEvent, (isLoading || usedSessionContext) ? 'new' : undefined)}
            className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2 flex-shrink-0"
            title={!canSubmit && !disabled ? getValidationMessage() || undefined : undefined}
          >
            <Send className="w-4 h-4" />
            <span>Send</span>
          </button>
        )}
      </form>
      {sessionLimitReached && (
        <p className="text-xs text-amber-600 mt-1">Session limit reached (5 turns). Start a new session.</p>
      )}
    </div>
  );
};
